import { useEffect, useMemo, useState } from "react";
import { Icon } from "./icons";
import { usePrefersReducedMotion } from "./hooks";
import { useContent } from "./content";

type Phase = "type" | "think" | "answer" | "hold";

/** Split an answer on `**hot**` markers into words, each flagged for accent. */
function tokenizeAnswer(a: string): { w: string; hot: boolean }[] {
  const out: { w: string; hot: boolean }[] = [];
  a.split("**").forEach((part, pi) => {
    const hot = pi % 2 === 1;
    part.split(" ").forEach((w) => {
      if (w !== "") out.push({ w, hot });
    });
  });
  return out;
}

/**
 * Auto-playing assistant demo: a question types in, the read-only tools light
 * up one by one, then the answer streams word-by-word with key numbers in the
 * accent. Cycles through the conversations. SSR-safe (initial render is the
 * static "typing" state, identical on server + client) and honours reduced
 * motion (snaps to the full first answer, no loop).
 */
function AssistantMock() {
  const t = useContent().assistant;
  const convos = t.convos;
  const reduce = usePrefersReducedMotion();

  const [ci, setCi] = useState(0);
  const [phase, setPhase] = useState<Phase>("type");
  const [typed, setTyped] = useState(0);
  const [toolsN, setToolsN] = useState(0);
  const [ansN, setAnsN] = useState(0);

  useEffect(() => {
    const first = convos[0];
    if (!first) return;
    if (reduce) {
      setCi(0);
      setPhase("answer");
      setTyped(first.q.length);
      setToolsN(first.tools.length);
      setAnsN(9999);
      return;
    }
    let alive = true;
    const timers: number[] = [];
    const wait = (ms: number) =>
      new Promise<void>((r) => {
        timers.push(window.setTimeout(r, ms));
      });
    (async () => {
      let idx = 0;
      await wait(400);
      while (alive) {
        const cv = convos[idx];
        if (!cv) break;
        setCi(idx);
        setPhase("type");
        setTyped(0);
        setToolsN(0);
        setAnsN(0);
        await wait(250);
        if (!alive) return;
        for (let k = 1; k <= cv.q.length; k++) {
          setTyped(k);
          await wait(22);
          if (!alive) return;
        }
        await wait(380);
        if (!alive) return;
        setPhase("think");
        for (let k = 1; k <= cv.tools.length; k++) {
          setToolsN(k);
          await wait(600);
          if (!alive) return;
        }
        await wait(260);
        if (!alive) return;
        setPhase("answer");
        const words = tokenizeAnswer(cv.a).length;
        for (let k = 1; k <= words; k++) {
          setAnsN(k);
          await wait(48);
          if (!alive) return;
        }
        setPhase("hold");
        await wait(3000);
        if (!alive) return;
        idx = (idx + 1) % convos.length;
      }
    })();
    return () => {
      alive = false;
      timers.forEach((id) => clearTimeout(id));
    };
  }, [reduce, convos]);

  const c = convos[Math.min(ci, convos.length - 1)] ?? convos[0];
  const tokens = useMemo(() => (c ? tokenizeAnswer(c.a) : []), [c]);
  if (!c) return null;

  const showAI = phase !== "type";
  const showAnswer = phase === "answer" || phase === "hold";

  return (
    <div className="frame asst">
      <div className="frame__bar asst__bar">
        <div className="frame__dots"><i /><i /><i /></div>
        <div className="asst__brand"><Icon name="sparkles" size={14} />TRACE Assistant</div>
        <span className="frame__tag">read-only · festival-scoped</span>
      </div>
      <div className="asst__body">
        <div className="asst__thread">
          <div className="asst__row asst__row--user">
            <div className="asst__bubble asst__bubble--user">
              {c.q.slice(0, typed)}
              {phase === "type" && <span className="asst__caret" />}
            </div>
          </div>
          {showAI && (
            <div className="asst__row asst__row--ai">
              <span className="asst__avatar"><Icon name="sparkles" size={13} /></span>
              <div className="asst__bubble asst__bubble--ai">
                <div className="asst__tools">
                  {c.tools.map((tool, i) => (
                    <span
                      key={tool}
                      className={
                        "asst__tool" +
                        (i < toolsN ? " done" : "") +
                        (i === toolsN && phase === "think" ? " run" : "")
                      }
                    >
                      <span className="asst__toolico">
                        {i < toolsN ? <Icon name="check" size={11} /> : null}
                      </span>
                      {tool}
                    </span>
                  ))}
                </div>
                {showAnswer ? (
                  <p className="asst__answer">
                    {tokens.slice(0, ansN).map((tk, i) => (
                      <span key={i} className={tk.hot ? "asst__hot" : undefined}>
                        {tk.w}{" "}
                      </span>
                    ))}
                    {phase === "answer" && <span className="asst__caret asst__caret--ai" />}
                  </p>
                ) : (
                  <div className="asst__typing" aria-hidden="true"><i /><i /><i /></div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="asst__inputbar">
          <span className="asst__inputico"><Icon name="sparkles" size={14} /></span>
          <span className="asst__placeholder">{t.placeholder}</span>
          <span className="asst__send"><Icon name="arrowRight" size={15} /></span>
        </div>
      </div>
    </div>
  );
}

export function AssistantSection() {
  const t = useContent().assistant;
  return (
    <section className="cue" id="cue-ask" data-cue="ASK">
      <div className="v2-wrap">
        <div className="cue__head" data-reveal>
          <span className="cue__sig">SIG.{t.sig}</span>
          <h2 className="cue__title">{t.title} <span className="dim">{t.dim}</span></h2>
          <span className="cue__rule" />
        </div>
        <p className="cue__lead" data-reveal>{t.lead}</p>
        <div className="asst-wrap" data-reveal><AssistantMock /></div>
      </div>
    </section>
  );
}
